import { useState } from 'react';
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  LinearProgress,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useGetResumoAvaliacoesLojaQuery } from '../api';
import { CabecalhoModal } from './cabecalho-modal';

/**
 * Amarelo da nota. Não é `warning.main` do tema (que é laranja): estrela de
 * avaliação é lida como amarelo, e o laranja competia com o vermelho das
 * promoções ao lado.
 */
const AMARELO_NOTA = '#FFC107';

const formatadorData = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/**
 * Média de avaliações da loja atual, clicável: abre o Dialog com a lista pública
 * (só o que o gestor não ocultou).
 *
 * Duas formas: `inline` (estrela + nota, para ficar colado no nome da loja) e
 * `chip` (contornado, com o total entre parênteses) para quando estiver sozinho.
 */
export function BadgeAvaliacoes({ variante = 'chip' }: { variante?: 'chip' | 'inline' }): JSX.Element | null {
  const { data, isLoading } = useGetResumoAvaliacoesLojaQuery();
  const [aberto, setAberto] = useState(false);

  if (isLoading) {
    return <CircularProgress size={18} />;
  }
  if (!data || data.total === 0) {
    // Loja nova: não exibe nada — evita ruído.
    return null;
  }

  return (
    <>
      {variante === 'inline' ? (
        <ButtonBase
          onClick={() => setAberto(true)}
          aria-label={`Avaliações da loja: ${data.media.toFixed(1)} de 5, ${data.total} avaliações`}
          sx={{
            gap: 0.5,
            px: 0.5,
            borderRadius: 1,
            color: AMARELO_NOTA,
            flexShrink: 0,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <StarIcon sx={{ fontSize: 20 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {data.media.toFixed(1)}
          </Typography>
        </ButtonBase>
      ) : (
        <Chip
          icon={<StarIcon sx={{ color: `${AMARELO_NOTA} !important` }} />}
          label={
            <Stack direction="row" spacing={0.5} alignItems="baseline">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {data.media.toFixed(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({data.total})
              </Typography>
            </Stack>
          }
          onClick={() => setAberto(true)}
          clickable
          variant="outlined"
          sx={{ height: 32, '& .MuiChip-icon': { fontSize: 18 } }}
        />
      )}

      <Dialog open={aberto} onClose={() => setAberto(false)} fullWidth maxWidth="sm" scroll="paper">
        <CabecalhoModal titulo="Avaliações" aoFechar={() => setAberto(false)} />
        <DialogContent>
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                <Typography variant="h3" sx={{ lineHeight: 1, fontWeight: 600 }}>
                  {data.media.toFixed(1)}
                </Typography>
                <Rating value={data.media} precision={0.1} readOnly size="small" />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {data.total} avaliações
                </Typography>
              </Box>
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                {([5, 4, 3, 2, 1] as const).map((n) => {
                  const qtd = data.distribuicao[n];
                  const pct = data.total === 0 ? 0 : (qtd / data.total) * 100;
                  return (
                    <Stack key={n} direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" sx={{ minWidth: 14 }}>
                        {n}
                      </Typography>
                      <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'action.hover',
                          '& .MuiLinearProgress-bar': { bgcolor: 'warning.main' },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 24, textAlign: 'right' }}>
                        {qtd}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {data.avaliacoes.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
              Nenhuma avaliação ainda.
            </Typography>
          ) : (
            <Stack divider={<Divider />} spacing={0}>
              {data.avaliacoes.map((a) => (
                <Box key={a.id} sx={{ py: 1.5 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 0.5 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2">{a.clienteNome ?? 'Cliente'}</Typography>
                      <Rating value={a.estrelas} readOnly size="small" />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {formatadorData.format(new Date(a.dataAvaliacao))}
                    </Typography>
                  </Stack>
                  {a.comentario && (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {a.comentario}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
